/**
 * Professional HH:MM interval picker.
 * Each segment supports: manual typing, arrow keys, scroll, and a clickable dropdown.
 *  */

import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DropdownList, SegmentInput } from './TimeIntervalDropdown';

export interface TimeIntervalPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function parseValue(raw: string): { h: number; m: number } {
  const match = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!match) return { h: 0, m: 0 };
  return { h: clamp(parseInt(match[1], 10), 0, 23), m: clamp(parseInt(match[2], 10), 0, 59) };
}

type Segment = 'h' | 'm';
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export const TimeIntervalPicker: FC<TimeIntervalPickerProps> = ({ value, onChange, disabled = false, className }) => {
  const { h, m } = parseValue(value);
  const [rawH, setRawH] = useState<string | null>(null);
  const [rawM, setRawM] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<Segment | null>(null);
  const hRef = useRef<HTMLInputElement>(null);
  const mRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const commit = useCallback(
    (nextH: number, nextM: number) => onChange(`${pad(nextH)}:${pad(nextM)}`),
    [onChange],
  );

  useEffect(() => {
    if (!openDropdown) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  const handleHBlur = () => { const next = rawH !== null ? clamp(parseInt(rawH || '0', 10), 0, 23) : h; setRawH(null); commit(next, m); };
  const handleMBlur = () => { const next = rawM !== null ? clamp(parseInt(rawM || '0', 10), 0, 59) : m; setRawM(null); commit(h, next); };

  const handleHChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
    setRawH(digits);
    if (digits.length === 2) { commit(clamp(parseInt(digits, 10), 0, 23), m); setRawH(null); mRef.current?.focus(); }
  };
  const handleMChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
    setRawM(digits);
    if (digits.length === 2) { commit(h, clamp(parseInt(digits, 10), 0, 59)); setRawM(null); }
  };

  const handleKeyDown = (seg: Segment) => (e: React.KeyboardEvent) => {
    const isH = seg === 'h';
    if (e.key === 'ArrowUp') { e.preventDefault(); commit(isH ? clamp(h + 1, 0, 23) : h, isH ? m : clamp(m + 1, 0, 59)); }
    if (e.key === 'ArrowDown') { e.preventDefault(); commit(isH ? clamp(h - 1, 0, 23) : h, isH ? m : clamp(m - 1, 0, 59)); }
    if (e.key === 'ArrowRight' && isH) { e.preventDefault(); mRef.current?.focus(); }
    if (e.key === 'ArrowLeft' && !isH) { e.preventDefault(); hRef.current?.focus(); }
  };

  const handleWheel = (seg: Segment) => (e: React.WheelEvent) => {
    const ref = seg === 'h' ? hRef : mRef;
    if (document.activeElement !== ref.current) return;
    e.preventDefault();
    if (seg === 'h') commit(clamp(h + (e.deltaY < 0 ? 1 : -1), 0, 23), m);
    else commit(h, clamp(m + (e.deltaY < 0 ? 1 : -1), 0, 59));
  };

  const displayH = rawH !== null ? rawH : pad(h);
  const displayM = rawM !== null ? rawM : pad(m);

  return (
    <div ref={containerRef} className={cn('relative inline-flex', className)}>
      <div className={cn(
        'flex h-10 items-center gap-0.5 rounded-md border border-input bg-background px-1.5 text-sm ring-offset-background transition-colors',
        !disabled && 'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        disabled && 'cursor-not-allowed opacity-50',
      )}>
        <SegmentInput ref={hRef} value={displayH} onChange={handleHChange}
          onFocus={() => { setRawH(''); setTimeout(() => hRef.current?.select(), 0); }}
          onBlur={handleHBlur} onKeyDown={handleKeyDown('h')} onWheel={handleWheel('h')} disabled={disabled} aria-label="Hours" />
        <DropdownToggle disabled={disabled} onClick={() => setOpenDropdown(openDropdown === 'h' ? null : 'h')} label="Select hours" />
        <span className="select-none font-mono text-muted-foreground">:</span>
        <SegmentInput ref={mRef} value={displayM} onChange={handleMChange}
          onFocus={() => { setRawM(''); setTimeout(() => mRef.current?.select(), 0); }}
          onBlur={handleMBlur} onKeyDown={handleKeyDown('m')} onWheel={handleWheel('m')} disabled={disabled} aria-label="Minutes" />
        <DropdownToggle disabled={disabled} onClick={() => setOpenDropdown(openDropdown === 'm' ? null : 'm')} label="Select minutes" />
      </div>
      {openDropdown && (
        <DropdownList
          items={openDropdown === 'h' ? HOURS : MINUTES}
          selected={openDropdown === 'h' ? h : m}
          onSelect={(val) => { if (openDropdown === 'h') commit(val, m); else commit(h, val); setOpenDropdown(null); }}
          align={openDropdown === 'h' ? 'left' : 'right'}
        />
      )}
    </div>
  );
};

function DropdownToggle({ disabled, onClick, label }: { disabled: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" tabIndex={-1} disabled={disabled} onClick={onClick} aria-label={label}
      className="flex h-6 w-4 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none">
      <ChevronDown className="h-3 w-3" />
    </button>
  );
}
